/**
 * API Service - Centrale configuratie voor backend communicatie
 */

// Dynamisch bepalen van API URL gebaseerd op omgeving
export const getApiBaseUrl = (): string => {
  // Development (localhost:5055) -> gebruik localhost:5050 
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:5050';
  }
  // Network development (192.168.x.x) -> gebruik IP:5050 voor mobiel testen
  if (window.location.hostname.startsWith('192.168.')) {
    return `http://${window.location.hostname}:5050`;
  }
  // Productie -> zelfde origin (API via Apache ProxyPass)
  return '';
};

const API_BASE_URL = getApiBaseUrl();

// Token management
export const getToken = (): string | null => {
  return localStorage.getItem('access_token');
};

export const setToken = (token: string): void => {
  localStorage.setItem('access_token', token);
  // Dispatch custom event for same-tab updates
  window.dispatchEvent(new CustomEvent('tokenChanged', { detail: { token } }));
};

export const removeToken = (): void => {
  localStorage.removeItem('access_token');
  // Dispatch custom event for same-tab updates
  window.dispatchEvent(new CustomEvent('tokenChanged', { detail: { token: null } }));
};

// Token refresh functie - vernieuwt de token na elke succesvolle API call
let isRefreshing = false;
const refreshToken = async (): Promise<void> => {
  if (isRefreshing) return;

  const token = getToken();
  if (!token) return;

  isRefreshing = true;
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (response.ok) {
      const data = await response.json();
      setToken(data.access_token);
    }
  } catch {
    // Silently fail - token refresh is not critical
  } finally {
    isRefreshing = false;
  }
};

// API fetch wrapper
export const apiFetch = async (
  endpoint: string,
  options: RequestInit = {}
): Promise<any> => {
  try {
    const token = getToken();

    /* Bij een upload moet de browser zelf de Content-Type zetten, inclusief de
       multipart-boundary. Zetten wij hier application/json, dan komt het formulier
       als onleesbare body binnen en meldt FastAPI dat alle velden ontbreken.
       `headers: {}` meegeven hielp niet: dat voegt samen, het vervangt niet.
       Gevonden 23-09-2026 bij het uploaden van een scheepsdocument. */
    const isFormData = options.body instanceof FormData;

    const headers: Record<string, string> = isFormData
      ? {}
      : { 'Content-Type': 'application/json' };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Merge with any provided headers
    if (options.headers) {
      Object.assign(headers, options.headers);
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    if (response.status === 401) {
      // Token expired or invalid
      removeToken();
      // Redirect naar login - gebruik relative path die werkt met basename
      window.location.href = '/login';
      throw new Error('Unauthorized');
    }

    if (!response.ok) {
      let error;
      try {
        error = await response.json();
      } catch {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Handle FastAPI validation errors (422)
      if (response.status === 422 && Array.isArray(error.detail)) {
        const validationErrors = error.detail
          .map((err: any) => `${err.loc.join('.')}: ${err.msg}`)
          .join(', ');
        throw new Error(validationErrors);
      }

      // Handle other errors
      if (typeof error.detail === 'string') {
        throw new Error(error.detail);
      }

      throw new Error('API Error');
    }

    // Bij succesvolle API call: refresh token (sliding expiration)
    // Niet refreshen bij auth endpoints zelf
    if (token && !endpoint.includes('/auth/')) {
      refreshToken();
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return null;
    }

    return response.json();
  } catch (error: any) {
    // Network errors, CORS errors, etc.
    if (error.message === 'Unauthorized') {
      throw error; // Re-throw auth errors
    }

    if (error.message.includes('Failed to fetch') || error.name === 'TypeError') {
      throw new Error('Kan geen verbinding maken met de server. Controleer of de backend draait.');
    }

    throw error;
  }
};

export default apiFetch;
